import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiX, FiCopy, FiEye, FiList, FiFileText, FiClock, FiTrendingUp } from 'react-icons/fi';
import { brokerageApi } from '../../api';
import { showToast } from '../../utils/toast';

type AccountRow = Record<string, unknown>;
type OrderRow = Record<string, unknown>;
type PositionRow = Record<string, unknown>;

const CASH_ACCOUNT_ID = '1AHOD4917PPS21QBDA56P53F0B';

const pickPreferredAccountId = (list: AccountRow[]) => {
  const byId = list.find(
    (a) => String(a.account_id || a.accountId || '') === CASH_ACCOUNT_ID,
  );
  if (byId) return CASH_ACCOUNT_ID;

  const cash = list.find((a) => {
    const type = String(a.account_type || a.accountType || '').toUpperCase();
    const name = String(
      a.name || a.account_label || a.accountLabel || a.account_name || '',
    ).toUpperCase();
    return type.includes('CASH') || name.includes('CASH');
  });
  if (cash) return String(cash.account_id || cash.accountId || '');

  return String(list[0]?.account_id || list[0]?.accountId || '');
};

const ConnectWebull: React.FC = () => {
  const navigate = useNavigate();
  const [isConnecting, setIsConnecting] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [loadingToken, setLoadingToken] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [accessTokenExpiresAt, setAccessTokenExpiresAt] = useState<string | null>(null);

  const [showAccountsModal, setShowAccountsModal] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accounts, setAccounts] = useState<AccountRow[]>([]);

  const [showOrderModal, setShowOrderModal] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [orderAccessToken, setOrderAccessToken] = useState('');
  const [orderAccountId, setOrderAccountId] = useState('');
  const [orderClientId, setOrderClientId] = useState('');
  const [orderDetail, setOrderDetail] = useState<Record<string, unknown> | null>(null);

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyAccountId, setHistoryAccountId] = useState(CASH_ACCOUNT_ID);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyOrders, setHistoryOrders] = useState<OrderRow[]>([]);

  const [showTradeModal, setShowTradeModal] = useState(false);
  const [tradeAccountId, setTradeAccountId] = useState(CASH_ACCOUNT_ID);
  const [tradeSymbol, setTradeSymbol] = useState('SPXW7768');
  const [placingTrade, setPlacingTrade] = useState(false);
  const [lastResolved, setLastResolved] = useState<string | null>(null);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [positions, setPositions] = useState<PositionRow[]>([]);
  const [openOrders, setOpenOrders] = useState<OrderRow[]>([]);
  const [closingKey, setClosingKey] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('webull');
    if (!status) return;

    if (status === 'connected') {
      showToast('success', 'Webull connected — tokens saved');
    } else if (status === 'error') {
      showToast('error', decodeURIComponent(params.get('message') || 'Webull connection failed'));
    }
    navigate('/dashboard/connect-webull', { replace: true });
  }, [navigate]);

  const ensureAccounts = async () => {
    if (accounts.length) return accounts;
    // No per-account balances — avoids sandbox 429 when opening Place Trade / History
    const result = await brokerageApi.getWebullAccounts({ includeBalances: false });
    if (!result.success) {
      throw new Error(result.error || 'Failed to list Webull accounts');
    }
    const list = result.accounts || [];
    setAccounts(list);
    return list;
  };

  const handleConnectWebull = async () => {
    try {
      setIsConnecting(true);
      showToast('info', 'Opening Webull authorization…');

      const result = await brokerageApi.getWebullConnectUrl();

      if (result.success && result.redirectURI) {
        window.location.href = result.redirectURI;
        return;
      }

      showToast('error', result.error || 'Failed to open Webull authorization');
      setIsConnecting(false);
    } catch (error: any) {
      console.error('Connect Webull error:', error);
      showToast(
        'error',
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          'Failed to connect Webull',
      );
      setIsConnecting(false);
    }
  };

  const handleShowAccessToken = async () => {
    try {
      setLoadingToken(true);
      setShowTokenModal(true);
      setAccessToken(null);
      setAccessTokenExpiresAt(null);

      const result = await brokerageApi.getWebullAccessToken();

      if (!result.success || !result.accessToken) {
        showToast('error', result.error || 'Failed to load access token');
        setShowTokenModal(false);
        return;
      }

      setAccessToken(result.accessToken);
      setAccessTokenExpiresAt(result.accessTokenExpiresAt || null);
    } catch (error: any) {
      console.error('Show access token error:', error);
      showToast(
        'error',
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          'Failed to load access token',
      );
      setShowTokenModal(false);
    } finally {
      setLoadingToken(false);
    }
  };

  const handleListAccounts = async () => {
    try {
      setLoadingAccounts(true);
      setShowAccountsModal(true);
      setAccounts([]);

      const result = await brokerageApi.getWebullAccounts({ includeBalances: true });

      if (!result.success) {
        showToast('error', result.error || 'Failed to list Webull accounts');
        setShowAccountsModal(false);
        return;
      }

      setAccounts(result.accounts || []);
      if (!(result.accounts || []).length) {
        showToast('info', 'No Webull accounts returned');
      }
    } catch (error: any) {
      console.error('List Webull accounts error:', error);
      showToast(
        'error',
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          'Failed to list Webull accounts',
      );
      setShowAccountsModal(false);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleCopyToken = async () => {
    if (!accessToken) return;
    try {
      await navigator.clipboard.writeText(accessToken);
      showToast('success', 'Access token copied');
    } catch {
      showToast('error', 'Could not copy token');
    }
  };

  const openOrderDetailModal = () => {
    setShowOrderModal(true);
    setOrderDetail(null);
    if (accessToken) setOrderAccessToken(accessToken);
  };

  const handleFetchOrderDetail = async () => {
    const token = orderAccessToken.trim();
    const accountId = orderAccountId.trim();
    const clientOrderId = orderClientId.trim();

    if (!token || !accountId) {
      showToast('error', 'Access token and account id are required');
      return;
    }

    try {
      setLoadingOrder(true);
      setOrderDetail(null);
      const result = await brokerageApi.getWebullOrderDetail({
        accessToken: token,
        accountId,
        clientOrderId,
      });

      if (!result.success || !result.order) {
        showToast('error', result.error || 'Failed to fetch order detail');
        return;
      }

      setOrderDetail(result.order);
      showToast('success', 'Order detail loaded');
    } catch (error: any) {
      console.error('Order detail error:', error);
      showToast(
        'error',
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          'Failed to fetch order detail',
      );
    } finally {
      setLoadingOrder(false);
    }
  };

  const openHistoryModal = async () => {
    setShowHistoryModal(true);
    setHistoryOrders([]);
    try {
      setLoadingAccounts(true);
      const list = await ensureAccounts();
      const preferred = pickPreferredAccountId(list);
      if (!historyAccountId && preferred) setHistoryAccountId(preferred);
    } catch (error: any) {
      showToast('error', error.message || 'Failed to load accounts');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleLoadHistory = async () => {
    const accountId = historyAccountId.trim();
    if (!accountId) {
      showToast('error', 'Select an account');
      return;
    }
    try {
      setLoadingHistory(true);
      setHistoryOrders([]);
      const result = await brokerageApi.getWebullOrderHistory(accountId);
      if (!result.success) {
        showToast('error', result.error || 'Failed to load history');
        return;
      }
      setHistoryOrders(result.orders || []);
      showToast('success', `${(result.orders || []).length} trades (last ~7 days)`);
    } catch (error: any) {
      showToast(
        'error',
        error.response?.data?.error || error.message || 'Failed to load history',
      );
    } finally {
      setLoadingHistory(false);
    }
  };

  const refreshTradeLists = async (accountId: string) => {
    if (!accountId) return;
    setLoadingPositions(true);
    try {
      const [posRes, openRes] = await Promise.all([
        brokerageApi.getWebullPositions(accountId),
        brokerageApi.getWebullOpenOrders(accountId),
      ]);
      setPositions(posRes.success ? posRes.positions || [] : []);
      setOpenOrders(openRes.success ? openRes.orders || [] : []);
      if (!posRes.success && !openRes.success) {
        showToast('error', posRes.error || openRes.error || 'Failed to load positions');
      }
    } catch (error: any) {
      setPositions([]);
      setOpenOrders([]);
      showToast(
        'error',
        error.response?.data?.error || error.message || 'Failed to load positions',
      );
    } finally {
      setLoadingPositions(false);
    }
  };

  const openTradeModal = async () => {
    setShowTradeModal(true);
    setPositions([]);
    setOpenOrders([]);
    try {
      setLoadingAccounts(true);
      const list = await ensureAccounts();
      const preferred = pickPreferredAccountId(list);
      const accountId = tradeAccountId || preferred;
      if (!tradeAccountId && preferred) setTradeAccountId(preferred);
      if (accountId) await refreshTradeLists(accountId);
    } catch (error: any) {
      showToast('error', error.message || 'Failed to load accounts');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handlePlaceTrade = async () => {
    const accountId = tradeAccountId.trim();
    const symbol = tradeSymbol.trim();
    if (!accountId || !symbol) {
      showToast('error', 'Account and symbol are required');
      return;
    }
    try {
      setPlacingTrade(true);
      setLastResolved(null);
      // Server defaults: BUY qty 1 CALL + nearest listed expiry for SPXW7768-style symbols
      const result = await brokerageApi.placeWebullOrder({
        accountId,
        symbol,
      });
      if (!result.success) {
        showToast('error', result.error || 'Place order failed');
        return;
      }
      const resolved = (result as { resolved?: { expiry?: string; optionType?: string; strike?: string } })
        .resolved;
      const detail = resolved?.expiry
        ? `BUY 1 ${resolved.optionType || 'CALL'} ${symbol} exp ${resolved.expiry}`
        : `BUY 1 ${symbol}`;
      setLastResolved(detail);
      showToast('success', `Placed: ${detail}`);
      await refreshTradeLists(accountId);
    } catch (error: any) {
      showToast(
        'error',
        error.response?.data?.error || error.message || 'Place order failed',
      );
    } finally {
      setPlacingTrade(false);
    }
  };

  const handleClosePosition = async (position: PositionRow, index: number) => {
    const accountId = tradeAccountId.trim();
    if (!accountId) return;

    const instrumentType = String(position.instrument_type || position.instrumentType || 'EQUITY');
    const symbol = String(position.symbol || '');
    const qty = Math.abs(Number(position.quantity || position.qty || 0));
    const legs = (position.legs as Array<Record<string, unknown>> | undefined) || [];
    const leg0 = legs[0] || {};
    const key = `${index}-${symbol}`;

    try {
      setClosingKey(key);
      const result = await brokerageApi.closeWebullPosition({
        accountId,
        symbol: String(leg0.symbol || symbol),
        quantity: qty,
        instrumentType,
        strike: (leg0.option_exercise_price ||
          leg0.strike_price ||
          position.strike_price ||
          position.strike) as string | number | undefined,
        expiry: String(
          leg0.option_expire_date ||
            position.option_expire_date ||
            position.expiry ||
            '',
        ),
        optionType: String(
          leg0.option_type || position.option_type || position.optionType || 'CALL',
        ),
        optionStrategy: String(position.option_strategy || position.optionStrategy || 'SINGLE'),
      });
      if (!result.success) {
        showToast('error', result.error || 'Close failed');
        return;
      }
      showToast('success', 'Close order placed');
      await refreshTradeLists(accountId);
    } catch (error: any) {
      showToast(
        'error',
        error.response?.data?.error || error.message || 'Close failed',
      );
    } finally {
      setClosingKey(null);
    }
  };

  const handleCancelOpenOrder = async (order: OrderRow) => {
    const accountId = tradeAccountId.trim();
    const clientOrderId = String(order.client_order_id || order.clientOrderId || '');
    if (!accountId || !clientOrderId) return;
    try {
      setClosingKey(`cancel-${clientOrderId}`);
      const result = await brokerageApi.cancelWebullOrder({ accountId, clientOrderId });
      if (!result.success) {
        showToast('error', result.error || 'Cancel failed');
        return;
      }
      showToast('success', 'Order cancelled');
      await refreshTradeLists(accountId);
    } catch (error: any) {
      showToast(
        'error',
        error.response?.data?.error || error.message || 'Cancel failed',
      );
    } finally {
      setClosingKey(null);
    }
  };

  const accountName = (account: AccountRow) =>
    String(
      account.name ||
        account.account_label ||
        account.accountLabel ||
        account.account_name ||
        account.accountName ||
        account.account_number ||
        account.accountNumber ||
        'Webull Account',
    );

  const accountMode = (account: AccountRow) => {
    if (account.accountMode === 'paper' || account.isPaper === true) return 'Paper';
    if (account.accountMode === 'real' || account.isPaper === false) return 'Real';
    const number = String(account.account_number || account.accountNumber || '');
    return /^DEV/i.test(number) ? 'Paper' : 'Real';
  };

  const formatMoney = (value: unknown, currency = 'USD') => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '—';
    return `${currency} ${num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  /** e.g. "SPX 7780 CALL" from position / order legs */
  const formatOptionFullName = (row: Record<string, unknown>) => {
    const legs = (row.legs as Array<Record<string, unknown>> | undefined) || [];
    const childOrders = (row.orders as Array<Record<string, unknown>> | undefined) || [];
    const leg = legs[0] || childOrders[0] || row;

    const symbol = String(
      row.symbol || row.ticker || leg.symbol || '—',
    ).toUpperCase();

    const strikeRaw =
      leg.strike_price ??
      leg.strikePrice ??
      leg.option_exercise_price ??
      row.strike_price ??
      row.strikePrice ??
      null;
    const strikeNum = Number(strikeRaw);
    const strike = Number.isFinite(strikeNum)
      ? String(strikeNum % 1 === 0 ? Math.trunc(strikeNum) : strikeNum)
      : strikeRaw != null && String(strikeRaw) !== ''
        ? String(strikeRaw).replace(/\.0+$/, '')
        : '';

    const cp = String(
      leg.option_type || leg.optionType || row.option_type || row.optionType || '',
    ).toUpperCase();
    const callPut =
      cp === 'PUT' || cp === 'P' ? 'PUT' : cp === 'CALL' || cp === 'C' ? 'CALL' : '';

    if (strike && callPut) return `${symbol} ${strike} ${callPut}`;
    if (strike) return `${symbol} ${strike}`;
    if (callPut) return `${symbol} ${callPut}`;
    const itype = String(row.instrument_type || row.instrumentType || '');
    return itype ? `${symbol} ${itype}` : symbol;
  };

  const orderTradeName = (order: OrderRow) => formatOptionFullName(order);

  const accountOptions = accounts.map((a, i) => {
    const id = String(a.account_id || a.accountId || '');
    return (
      <option key={id || i} value={id}>
        {accountName(a)} · {id}
      </option>
    );
  });

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center gap-3">
        <img src="/webull.png" alt="Webull" className="h-10 w-10 object-contain" />
        <h2 className="lg:text-3xl text-lg font-bold text-foreground">Connect Webull</h2>
      </div>

      <div className="max-w-lg relative overflow-hidden rounded-xl border-2 border-green-500/30 bg-gradient-to-br from-green-500/10 via-emerald-600/5 to-teal-500/10 p-6">
        <div className="flex items-center gap-3 mb-4">
          <img src="/webull.png" alt="Webull" className="w-14 h-14 object-contain" />
          <div>
            <h3 className="text-lg font-bold text-foreground">Webull</h3>
            <p className="text-xs text-muted">Authorize via Webull Connect API</p>
          </div>
        </div>
        <p className="text-sm text-muted mb-4">
          Connect your Webull account. You will be redirected to Webull to grant access.
        </p>
        <div className="space-y-3">
          <button
            type="button"
            onClick={handleConnectWebull}
            disabled={isConnecting}
            className="w-full py-2.5 px-4 rounded-lg font-medium transition-all bg-green-500 text-white hover:bg-green-600 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isConnecting ? 'Connecting...' : 'Connect Webull'}
          </button>
          <button
            type="button"
            onClick={handleShowAccessToken}
            disabled={loadingToken}
            className="w-full py-2.5 px-4 rounded-lg font-medium transition-all border border-green-500/50 text-foreground hover:bg-green-500/10 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            <FiEye className="h-4 w-4" />
            {loadingToken ? 'Loading…' : 'Show Access Token'}
          </button>
          <button
            type="button"
            onClick={handleListAccounts}
            disabled={loadingAccounts}
            className="w-full py-2.5 px-4 rounded-lg font-medium transition-all border border-green-500/50 text-foreground hover:bg-green-500/10 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            <FiList className="h-4 w-4" />
            {loadingAccounts ? 'Loading…' : 'List Accounts'}
          </button>
          <button
            type="button"
            onClick={openHistoryModal}
            className="w-full py-2.5 px-4 rounded-lg font-medium transition-all border border-green-500/50 text-foreground hover:bg-green-500/10 inline-flex items-center justify-center gap-2"
          >
            <FiClock className="h-4 w-4" />
            See History
          </button>
          <button
            type="button"
            onClick={openTradeModal}
            className="w-full py-2.5 px-4 rounded-lg font-medium transition-all border border-green-500/50 text-foreground hover:bg-green-500/10 inline-flex items-center justify-center gap-2"
          >
            <FiTrendingUp className="h-4 w-4" />
            Place Trade
          </button>
          <button
            type="button"
            onClick={openOrderDetailModal}
            className="w-full py-2.5 px-4 rounded-lg font-medium transition-all border border-green-500/50 text-foreground hover:bg-green-500/10 inline-flex items-center justify-center gap-2"
          >
            <FiFileText className="h-4 w-4" />
            Get Order Detail
          </button>
        </div>
      </div>

      {showTokenModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-xl max-w-lg w-full border border-border">
            <div className="border-b border-border px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Webull Access Token</h3>
              <button
                type="button"
                onClick={() => setShowTokenModal(false)}
                className="text-muted hover:text-foreground transition-colors"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {loadingToken ? (
                <p className="text-sm text-muted">Loading access token…</p>
              ) : (
                <>
                  <div className="rounded-lg border border-border bg-surface p-3 break-all font-mono text-xs text-foreground max-h-48 overflow-y-auto">
                    {accessToken || '—'}
                  </div>
                  {accessTokenExpiresAt && (
                    <p className="text-xs text-muted">
                      Expires: {new Date(accessTokenExpiresAt).toLocaleString()}
                    </p>
                  )}
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setShowTokenModal(false)}
                      className="btn-secondary flex-1"
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      onClick={handleCopyToken}
                      disabled={!accessToken}
                      className="btn-primary flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      <FiCopy className="h-4 w-4" />
                      Copy
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showAccountsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-xl max-w-lg w-full border border-border max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-background border-b border-border px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Webull Accounts</h3>
              <button
                type="button"
                onClick={() => setShowAccountsModal(false)}
                className="text-muted hover:text-foreground transition-colors"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {loadingAccounts ? (
                <p className="text-sm text-muted">Loading accounts…</p>
              ) : accounts.length === 0 ? (
                <p className="text-sm text-muted text-center py-4">No accounts found.</p>
              ) : (
                <div className="space-y-3">
                  {accounts.map((account, index) => {
                    const balance = (account.balance || null) as Record<string, unknown> | null;
                    const currency = String(balance?.currency || 'USD');
                    const mode = accountMode(account);
                    const isPaper = mode === 'Paper';
                    return (
                      <div
                        key={String(account.account_id || account.accountId || index)}
                        className="rounded-lg border border-border bg-surface p-4 space-y-2"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-semibold text-foreground">{accountName(account)}</div>
                            <div className="text-xs text-muted mt-0.5">
                              {String(account.account_number || account.accountNumber || '—')}
                              {account.account_type || account.accountType
                                ? ` · ${String(account.account_type || account.accountType)}`
                                : ''}
                            </div>
                            <div className="text-[11px] text-muted mt-1 break-all font-mono">
                              Account ID:{' '}
                              {String(account.account_id || account.accountId || '—')}
                            </div>
                          </div>
                          <span
                            className={`shrink-0 px-2 py-1 rounded text-[11px] font-semibold uppercase tracking-wide ${
                              isPaper
                                ? 'bg-amber-500/15 text-amber-400'
                                : 'bg-green-500/15 text-green-400'
                            }`}
                          >
                            {mode}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <div className="text-[11px] text-muted uppercase tracking-wide">Balance</div>
                            <div className="font-medium text-foreground tabular-nums">
                              {formatMoney(balance?.netLiquidation ?? balance?.cashBalance, currency)}
                            </div>
                          </div>
                          <div>
                            <div className="text-[11px] text-muted uppercase tracking-wide">Cash</div>
                            <div className="font-medium text-foreground tabular-nums">
                              {formatMoney(balance?.cashBalance, currency)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <button
                type="button"
                onClick={() => setShowAccountsModal(false)}
                className="btn-secondary w-full"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showHistoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-xl max-w-lg w-full border border-border max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-background border-b border-border px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Trade History (7 days)</h3>
              <button
                type="button"
                onClick={() => setShowHistoryModal(false)}
                className="text-muted hover:text-foreground transition-colors"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Account</label>
                <select
                  value={historyAccountId}
                  onChange={(e) => setHistoryAccountId(e.target.value)}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-foreground"
                >
                  <option value="">Select account…</option>
                  {accountOptions}
                </select>
              </div>
              <button
                type="button"
                onClick={handleLoadHistory}
                disabled={loadingHistory || !historyAccountId}
                className="w-full py-2.5 px-4 rounded-lg font-medium bg-green-500 text-white hover:bg-green-600 disabled:opacity-60"
              >
                {loadingHistory ? 'Loading…' : 'Load History'}
              </button>
              {historyOrders.length === 0 && !loadingHistory ? (
                <p className="text-sm text-muted text-center py-2">No trades loaded yet.</p>
              ) : (
                <div className="space-y-2">
                  {historyOrders.map((order, i) => (
                    <div
                      key={String(order.client_order_id || order.order_id || i)}
                      className="rounded-lg border border-border bg-surface p-3 text-sm"
                    >
                      <div className="font-semibold text-foreground">{orderTradeName(order)}</div>
                      <div className="text-xs text-muted mt-1">
                        {String(order.status || '—')} · qty{' '}
                        {String(order.filled_quantity || order.total_quantity || '—')} · avg{' '}
                        {String(order.avg_filled_price || order.filled_price || '—')}
                      </div>
                      <div className="text-[11px] text-muted mt-1">
                        {String(order.place_time || order.create_time || '—')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => setShowHistoryModal(false)}
                className="btn-secondary w-full"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showTradeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-xl max-w-lg w-full border border-border max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-background border-b border-border px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Place Trade</h3>
              <button
                type="button"
                onClick={() => setShowTradeModal(false)}
                className="text-muted hover:text-foreground transition-colors"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Account</label>
                <select
                  value={tradeAccountId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setTradeAccountId(id);
                    void refreshTradeLists(id);
                  }}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-foreground"
                >
                  <option value="">Select account…</option>
                  {accountOptions}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Symbol <span className="text-muted font-normal">(just type SPXW7768)</span>
                </label>
                <input
                  type="text"
                  value={tradeSymbol}
                  onChange={(e) => setTradeSymbol(e.target.value)}
                  placeholder="SPXW7768"
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-foreground uppercase"
                />
                <p className="text-[11px] text-muted mt-1.5">
                  Auto: BUY · qty 1 · CALL · next Mon/Wed/Fri expiry (SPXW)
                </p>
              </div>
              {lastResolved && (
                <p className="text-xs text-green-400">Last placed: {lastResolved}</p>
              )}
              <button
                type="button"
                onClick={handlePlaceTrade}
                disabled={placingTrade || !tradeAccountId}
                className="w-full py-2.5 px-4 rounded-lg font-medium bg-green-500 text-white hover:bg-green-600 disabled:opacity-60"
              >
                {placingTrade ? 'Resolving & placing…' : 'Place Market Order'}
              </button>

              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-foreground">Open Positions</h4>
                <button
                  type="button"
                  onClick={() => tradeAccountId && refreshTradeLists(tradeAccountId)}
                  disabled={loadingPositions || !tradeAccountId}
                  className="text-xs text-green-400 hover:underline disabled:opacity-50"
                >
                  {loadingPositions ? 'Refreshing…' : 'Refresh'}
                </button>
              </div>
              {positions.length === 0 ? (
                <p className="text-sm text-muted">No open positions.</p>
              ) : (
                <div className="space-y-2">
                  {positions.map((pos, i) => {
                    const key = `${i}-${String(pos.symbol || '')}`;
                    return (
                      <div
                        key={key}
                        className="rounded-lg border border-border bg-surface p-3 flex items-center justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <div className="font-semibold text-foreground truncate">
                            {formatOptionFullName(pos)}
                          </div>
                          <div className="text-xs text-muted">
                            qty {String(pos.quantity || '—')}
                            {pos.option_strategy ? ` · ${String(pos.option_strategy)}` : ''}
                            {pos.instrument_type
                              ? ` · ${String(pos.instrument_type)}`
                              : ''}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleClosePosition(pos, i)}
                          disabled={closingKey === key}
                          className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30 disabled:opacity-50"
                        >
                          {closingKey === key ? 'Closing…' : 'Close'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              <h4 className="text-sm font-semibold text-foreground">Working Orders</h4>
              {openOrders.length === 0 ? (
                <p className="text-sm text-muted">No working orders.</p>
              ) : (
                <div className="space-y-2">
                  {openOrders.map((order, i) => {
                    const coid = String(order.client_order_id || order.clientOrderId || i);
                    return (
                      <div
                        key={coid}
                        className="rounded-lg border border-border bg-surface p-3 flex items-center justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <div className="font-semibold text-foreground">{orderTradeName(order)}</div>
                          <div className="text-xs text-muted">
                            {String(order.status || '—')} · {String(order.side || '')}{' '}
                            {String(order.total_quantity || order.quantity || '')}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleCancelOpenOrder(order)}
                          disabled={closingKey === `cancel-${coid}`}
                          className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium border border-border text-foreground hover:bg-surface disabled:opacity-50"
                        >
                          {closingKey === `cancel-${coid}` ? '…' : 'Cancel'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              <button
                type="button"
                onClick={() => setShowTradeModal(false)}
                className="btn-secondary w-full"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showOrderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-xl max-w-lg w-full border border-border max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-background border-b border-border px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Get Order Detail</h3>
              <button
                type="button"
                onClick={() => setShowOrderModal(false)}
                className="text-muted hover:text-foreground transition-colors"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Access Token</label>
                <textarea
                  value={orderAccessToken}
                  onChange={(e) => setOrderAccessToken(e.target.value)}
                  rows={3}
                  placeholder="Paste Webull access token"
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-foreground font-mono"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Account ID</label>
                <input
                  type="text"
                  value={orderAccountId}
                  onChange={(e) => setOrderAccountId(e.target.value)}
                  placeholder="e.g. 01H6IAUUTJVH5U5JF1C4BIL4R8"
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-foreground"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Client Order ID <span className="text-muted font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={orderClientId}
                  onChange={(e) => setOrderClientId(e.target.value)}
                  placeholder="client_order_id (optional)"
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-foreground"
                />
              </div>

              <button
                type="button"
                onClick={handleFetchOrderDetail}
                disabled={loadingOrder}
                className="w-full py-2.5 px-4 rounded-lg font-medium transition-all bg-green-500 text-white hover:bg-green-600 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loadingOrder ? 'Fetching…' : 'Fetch Order Detail'}
              </button>

              {orderDetail && (
                <div className="rounded-lg border border-border bg-surface p-3">
                  <p className="text-xs text-muted mb-2 uppercase tracking-wide">Response</p>
                  <pre className="text-[11px] text-foreground overflow-x-auto whitespace-pre-wrap break-all max-h-64 overflow-y-auto">
                    {JSON.stringify(orderDetail, null, 2)}
                  </pre>
                </div>
              )}

              <button
                type="button"
                onClick={() => setShowOrderModal(false)}
                className="btn-secondary w-full"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectWebull;
